
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { useSubscription } from "@/hooks/use-subscription";
import { useAvatar } from "@/hooks/use-avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, Crown, CreditCard, Loader2, ChevronDown, Settings } from "lucide-react";

export function AuthButtons() {
  const { user, signOut } = useAuth();
  const { subscribed: isPremium } = useSubscription();
  const { avatarUrl } = useAvatar();
  const location = useLocation();
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const userName = user?.user_metadata?.first_name || null;
  const userLastName = user?.user_metadata?.last_name || null;
  const initials = userName
    ? `${userName.charAt(0)}${userLastName ? userLastName.charAt(0) : ""}`.toUpperCase()
    : user?.email?.charAt(0).toUpperCase() || "?";

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err: any) {
      toast.error("Could not open billing portal", { description: err.message });
    } finally {
      setIsLoadingPortal(false);
    }
  };

  if (location.pathname.includes("/auth")) {
    return null;
  }

  return (
    <div className="flex items-center gap-3">
      {user ? (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2 border-border hover:bg-accent"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-6 h-6 rounded-full object-cover shrink-0"
                />
              ) : (
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[10px] font-bold shrink-0">
                  {initials}
                </span>
              )}
              <span className="max-w-[120px] truncate">
                {userName || "Account"}
              </span>
              {isPremium && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                  <Crown className="h-2.5 w-2.5" />
                  Pro
                </span>
              )}
              <ChevronDown size={12} className="text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56 bg-background border border-border shadow-lg z-50">
            {/* User identity header */}
            <div className="px-3 py-2 border-b border-border flex items-center gap-3">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-9 h-9 rounded-full object-cover shrink-0 border border-border"
                />
              ) : (
                <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
                  {initials}
                </span>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">
                  {userName ? `Hi, ${userName}` : "My Account"}
                </p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                {isPremium && (
                  <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                    <Crown className="h-3 w-3" />
                    Premium
                  </span>
                )}
              </div>
            </div>

            {/* Dashboard */}
            <DropdownMenuItem asChild>
              <Link to="/dashboard" className="cursor-pointer flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" />
                <span>My Dashboard</span>
              </Link>
            </DropdownMenuItem>

            {/* Settings */}
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Subscription action */}
            {isPremium ? (
              <DropdownMenuItem
                onClick={handleManageSubscription}
                disabled={isLoadingPortal}
                className="cursor-pointer flex items-center gap-2 text-primary focus:text-primary"
              >
                {isLoadingPortal ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                <span>Manage subscription</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem asChild>
                <Link
                  to="/premium"
                  className="cursor-pointer flex items-center gap-2 text-amber-600 font-medium focus:text-amber-700"
                >
                  <Crown className="h-4 w-4 text-amber-500" />
                  <span>Go Premium</span>
                </Link>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {/* Sign out */}
            <DropdownMenuItem
              onClick={signOut}
              className="cursor-pointer flex items-center gap-2 text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <>
          <Button asChild variant="ghost" className="hidden md:flex hover:text-grandpal-green-dark">
            <Link to="/auth/login">Sign In</Link>
          </Button>
          <Button asChild className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover text-white">
            <Link to="/auth/signup">Sign up for Free</Link>
          </Button>
        </>
      )}
    </div>
  );
}
